import * as THREE from 'three'

/**
 * Renders ski tracks behind the skier — two parallel lines
 * that fade out over ~15 feet (4.5 meters).
 */

const TRACK_LENGTH = 200       // max number of track segments
const TRACK_SPACING = 0.15     // meters between samples
const SKI_SEPARATION = 0.18    // half-distance between left and right ski track
const TRACK_WIDTH = 0.12       // width of each track line — visible!
const FADE_DISTANCE = 15       // meters before track fades out (~50 feet, longer trail)

interface TrackPoint {
  x: number
  y: number
  z: number
  age: number  // distance traveled since this point was placed
}

export class SkiTracks {
  private scene: THREE.Scene
  private leftTrack: THREE.Mesh
  private rightTrack: THREE.Mesh
  private leftGeo: THREE.BufferGeometry
  private rightGeo: THREE.BufferGeometry
  private points: TrackPoint[] = []
  private lastPos = new THREE.Vector3()
  private initialized = false

  constructor(scene: THREE.Scene) {
    this.scene = scene

    const trackMat = new THREE.MeshBasicMaterial({
      color: 0x7090a8,
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      side: THREE.DoubleSide,
    })

    this.leftGeo = new THREE.BufferGeometry()
    this.rightGeo = new THREE.BufferGeometry()

    this.leftTrack = new THREE.Mesh(this.leftGeo, trackMat)
    this.rightTrack = new THREE.Mesh(this.rightGeo, trackMat.clone())

    this.leftTrack.frustumCulled = false
    this.rightTrack.frustumCulled = false

    scene.add(this.leftTrack)
    scene.add(this.rightTrack)
  }

  update(skierX: number, skierY: number, skierZ: number, isAirborne: boolean): void {
    const pos = new THREE.Vector3(skierX, skierY, skierZ)

    if (!this.initialized) {
      this.lastPos.copy(pos)
      this.initialized = true
      return
    }

    // Only add track points when on the ground and moving
    const dist = pos.distanceTo(this.lastPos)
    if (dist >= TRACK_SPACING && !isAirborne) {
      this.points.push({
        x: skierX,
        y: skierY + 0.02,  // slightly above terrain to avoid z-fighting
        z: skierZ,
        age: 0,
      })
      this.lastPos.copy(pos)
    }

    // Age all points by distance moved
    for (const p of this.points) {
      p.age += dist
    }

    // Remove old points
    this.points = this.points.filter(p => p.age < FADE_DISTANCE)

    // Rebuild track geometry
    this.rebuildTrack(this.leftGeo, -SKI_SEPARATION)
    this.rebuildTrack(this.rightGeo, SKI_SEPARATION)
  }

  private rebuildTrack(geo: THREE.BufferGeometry, xOffset: number): void {
    const count = this.points.length
    if (count < 2) {
      geo.setAttribute('position', new THREE.Float32BufferAttribute([], 3))
      return
    }

    // Build a ribbon from quads connecting consecutive points
    const verts: number[] = []
    const uvs: number[] = []

    for (let i = 0; i < count; i++) {
      const p = this.points[i]
      // Two vertices per point (left edge and right edge of the track ribbon)
      verts.push(p.x + xOffset - TRACK_WIDTH / 2, p.y, p.z)
      verts.push(p.x + xOffset + TRACK_WIDTH / 2, p.y, p.z)

      const t = p.age / FADE_DISTANCE  // 0 = fresh, 1 = about to disappear
      uvs.push(0, t)
      uvs.push(1, t)
    }

    // Build triangle indices for the ribbon
    const indices: number[] = []
    for (let i = 0; i < count - 1; i++) {
      const a = i * 2
      const b = a + 1
      const c = a + 2
      const d = a + 3
      indices.push(a, c, b)
      indices.push(b, c, d)
    }

    geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3))
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
    geo.setIndex(indices)
    geo.computeVertexNormals()
  }
}
